import {
  DiferencaCanalVendaVM, PreviaProdutoCanalVendaVM, SnapshotGrupoCanalVendaVM,
  SnapshotOpcaoCanalVendaVM, SnapshotProdutoCanalVendaVM
} from './canais-venda.models';

export interface OpcaoGrupoRevisaoCanalVenda {
  chave: string;
  nome: string;
  atual: number | null;
  anterior: number | null;
  acao: 'Novo' | 'Já vinculado' | 'Sem alterações' | 'Será atualizado' | 'Removido';
}

export interface GrupoRevisaoCanalVenda {
  chave: string;
  nome: string;
  minimo: number;
  maximo: number;
  obrigatorio: boolean;
  ordem: number;
  disponivel: boolean;
  removido: boolean;
  opcoes: OpcaoGrupoRevisaoCanalVenda[];
}

export function gruposRevisao(previa: PreviaProdutoCanalVendaVM): GrupoRevisaoCanalVenda[] {
  const atuais = previa.desejado?.estrutura?.gruposAdicionais ||
    (previa.operacao === 'DESATIVAR' ? previa.publicado?.estrutura?.gruposAdicionais : null) || [];
  const anteriores = previa.publicado?.estrutura?.gruposAdicionais || [];
  const gruposAnteriores = new Map(anteriores.map(grupo => [grupo.externalCode, grupo]));
  const gruposAtuais = new Map(atuais.map(grupo => [grupo.externalCode, grupo]));
  return [...atuais, ...anteriores.filter(grupo => !gruposAtuais.has(grupo.externalCode))]
    .map(grupo => {
      const atual: SnapshotGrupoCanalVendaVM | undefined = gruposAtuais.get(grupo.externalCode);
      const anterior = gruposAnteriores.get(grupo.externalCode);
      const opcoesAtuais = atual?.opcoes || [];
      const opcoesAnteriores = anterior?.opcoes || [];
      const anterioresPorCodigo = new Map(opcoesAnteriores.map(opcao => [opcao.externalCode, opcao]));
      const atuaisPorCodigo = new Map(opcoesAtuais.map(opcao => [opcao.externalCode, opcao]));
      const opcoes = [...opcoesAtuais, ...opcoesAnteriores.filter(opcao => !atuaisPorCodigo.has(opcao.externalCode))]
        .map(opcao => {
          const depois: SnapshotOpcaoCanalVendaVM | undefined = atuaisPorCodigo.get(opcao.externalCode);
          const antes = anterioresPorCodigo.get(opcao.externalCode);
          const acao = !antes ? (depois?.idExterno ? 'Já vinculado' : 'Novo') : !depois ? 'Removido' :
            antes.nome !== depois.nome || antes.preco !== depois.preco ||
            antes.ordem !== depois.ordem || antes.disponivel !== depois.disponivel
              ? 'Será atualizado' : 'Sem alterações';
          return {chave: opcao.externalCode, nome: (depois || antes || opcao).nome,
            atual: depois?.preco ?? null, anterior: antes?.preco ?? null, acao} as OpcaoGrupoRevisaoCanalVenda;
        });
      const exibido = atual || anterior || grupo;
      return {chave: grupo.externalCode, nome: exibido.nome, minimo: exibido.minimo,
        maximo: exibido.maximo, obrigatorio: exibido.obrigatorio, ordem: exibido.ordem,
        disponivel: exibido.disponivel, removido: !atual, opcoes};
    }).sort((a, b) => a.ordem - b.ordem);
}

export interface LinhaRevisaoCanalVenda {
  campo: string;
  acao: string;
  antes: string;
  depois: string;
  imagem: boolean;
  alterada: boolean;
  secao: 'PRODUTO' | 'IMAGEM' | 'ADICIONAIS';
}

const CAMPOS_PRECO = ['comercial.preco', 'comercial.precoOriginal', 'comercial.herdaPrecoBase'];
const CAMPOS_PRODUTO = [
  'conteudo.nome', 'conteudo.descricao', 'conteudo.categoriaExternaId', 'conteudo.imagem',
  ...CAMPOS_PRECO,
  'comercial.disponivel'
];
const CAMPOS_GRUPO = ['nome', 'minimo', 'maximo', 'obrigatorio', 'ordem', 'disponivel'];
const CAMPOS_OPCAO = ['nome', 'preco', 'ordem', 'disponivel'];
const ROTULOS: {[campo: string]: string} = {
  'conteudo.nome': 'Nome', 'conteudo.descricao': 'Descrição',
  'conteudo.categoriaExternaId': 'Categoria', 'conteudo.imagem': 'Imagem',
  'comercial.preco': 'Preço de venda', 'comercial.precoOriginal': 'Preço original',
  'comercial.herdaPrecoBase': 'Origem do preço',
  'comercial.disponivel': 'Disponibilidade no canal', externalCode: 'Identificação da integração',
  nome: 'Nome', minimo: 'Mínimo de escolhas', maximo: 'Máximo de escolhas',
  obrigatorio: 'Escolha obrigatória', ordem: 'Ordem', disponivel: 'Disponibilidade', preco: 'Preço'
};

export function areasAlteradasRevisao(previa: PreviaProdutoCanalVendaVM): string[] {
  const areas = new Set<string>();
  previa.diferencas.forEach(({caminho}) => {
    if(caminho === 'produto' || caminho === 'comercial.vender') return;
    if(caminho.startsWith('estrutura.')) areas.add('Adicionais');
    else if(caminho.startsWith('comercial.preco') || caminho === 'comercial.herdaPrecoBase') areas.add('Preço');
    else if(caminho.startsWith('comercial.')) areas.add('Disponibilidade');
    else areas.add(ROTULOS[caminho] || 'Identificação');
  });
  return Array.from(areas);
}

export function imagemRevisao(url: string | null | undefined): string {
  if(!url) return '';
  url = url.trim().replace(/^https?:\/\/[^/]+(\/images\/empresa\/.*)$/i, '$1');
  if(!url) return '';
  if(/^https?:\/\//i.test(url) || url.startsWith('/images/empresa/')) return url;
  return '/images/empresa/' + url.replace(/^\/+/, '');
}

// Os caminhos identificam recursos pelo externalCode, nunca pela posição no array.
function recurso(snapshot: SnapshotProdutoCanalVendaVM | null | undefined, caminho: string): any {
  if(!snapshot) return undefined;
  const grupo = /^estrutura\.gruposAdicionais\[([^\]]+)\]/.exec(caminho);
  if(grupo) {
    const encontrado = snapshot.estrutura.gruposAdicionais.find(item => item.externalCode === grupo[1]);
    const opcao = /\.opcoes\[([^\]]+)\]/.exec(caminho);
    const alvo: any = opcao ? encontrado?.opcoes.find(item => item.externalCode === opcao[1]) : encontrado;
    const campo = /\]\.([^.\[\]]+)$/.exec(caminho);
    return campo ? alvo?.[campo[1]] : alvo;
  }
  if(caminho === 'produto') return snapshot;
  return caminho.split('.').reduce((valor: any, campo) => valor?.[campo], snapshot);
}

function nomeCampo(previa: PreviaProdutoCanalVendaVM, caminho: string): string {
  const grupo = /^estrutura\.gruposAdicionais\[[^\]]+\]/.exec(caminho);
  if(!grupo) return ROTULOS[caminho] || 'Produto';
  const nomeGrupo = recurso(previa.desejado, grupo[0])?.nome || recurso(previa.publicado, grupo[0])?.nome || 'Grupo';
  const opcao = /^(estrutura\..+\.opcoes\[[^\]]+\])/.exec(caminho);
  const nomeOpcao = opcao && (recurso(previa.desejado, opcao[1])?.nome || recurso(previa.publicado, opcao[1])?.nome || 'Opção');
  const campo = /\]\.([^.\[\]]+)$/.exec(caminho);
  return [nomeGrupo, nomeOpcao, campo && (ROTULOS[campo[1]] || campo[1])].filter(Boolean).join(' · ');
}

function formateValor(caminho: string, valor: any, categorias: {[id: string]: string}): string {
  if(valor == null || valor === '') return '—';
  if(caminho === 'conteudo.imagem') return imagemRevisao(valor.url);
  if(caminho === 'conteudo.categoriaExternaId') return categorias[String(valor)] || `Categoria externa: ${valor}`;
  if(caminho === 'comercial.herdaPrecoBase') return valor ? 'Herdado do cadastro' : 'Específico do canal';
  if(caminho.endsWith('obrigatorio')) return valor ? 'Sim' : 'Não';
  if(typeof valor === 'boolean') return valor ? 'Ativo' : 'Pausado';
  if(typeof valor === 'number') return /\.(preco|precoOriginal)$/.test(caminho)
    ? valor.toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'}) : String(valor);
  if(typeof valor === 'object') {
    if(valor.nome) {
      const linhas = [String(valor.nome)];
      if(valor.opcoes) {
        linhas.push(`Mínimo: ${valor.minimo} · Máximo: ${valor.maximo} · ${valor.obrigatorio ? 'Obrigatório' : 'Opcional'}`);
        linhas.push(...valor.opcoes.map((opcao: any) =>
          `${opcao.nome} · ${formateValor('opcao.preco', opcao.preco, categorias)} · ${opcao.disponivel ? 'Ativa' : 'Pausada'}`));
      } else if(valor.preco != null) linhas.push(formateValor('opcao.preco', valor.preco, categorias));
      linhas.push(valor.disponivel ? 'Ativo' : 'Pausado');
      if(valor.ordem != null) linhas.push(`Ordem: ${valor.ordem}`);
      return linhas.join('\n');
    }
    return 'Detalhes disponíveis na versão completa';
  }
  return String(valor);
}

function formatePrecoRevisao(previa: PreviaProdutoCanalVendaVM, lado: 'antes' | 'depois'): string {
  const snapshot = lado === 'antes' ? previa.publicado : previa.desejado;
  const valores = CAMPOS_PRECO.map(caminho => snapshot
    ? recurso(snapshot, caminho) : previa.diferencas.find(item => item.caminho === caminho)?.[lado]);
  const [preco, original, herda] = valores;
  if(valores.every(valor => valor == null)) return '—';
  const valor = formateValor('comercial.preco', preco, {});
  const promocao = typeof original === 'number' && typeof preco === 'number' && original > preco;
  const resumo = promocao ? `De ${formateValor('comercial.precoOriginal', original, {})} por ${valor}` : valor;
  return typeof herda === 'boolean'
    ? `${resumo}\n${herda ? 'Herdado do cadastro' : 'Específico do canal'}` : resumo;
}

export function linhasRevisao(
  previa: PreviaProdutoCanalVendaVM, completo = false, categorias: {[id: string]: string} = {}
): LinhaRevisaoCanalVenda[] {
  const criando = previa.operacao === 'CRIAR';
  let diferencas = previa.diferencas.filter(item => item.caminho !== 'comercial.vender');
  if(completo || criando) {
    const caminhos = new Set(CAMPOS_PRODUTO);
    [previa.publicado, previa.desejado].forEach(snapshot => {
      snapshot?.estrutura.gruposAdicionais.forEach(grupo => {
        const prefixo = `estrutura.gruposAdicionais[${grupo.externalCode}]`;
        CAMPOS_GRUPO.forEach(campo => caminhos.add(`${prefixo}.${campo}`));
        grupo.opcoes.forEach(opcao => CAMPOS_OPCAO.forEach(campo =>
          caminhos.add(`${prefixo}.opcoes[${opcao.externalCode}].${campo}`)));
      });
    });
    diferencas = Array.from(caminhos).map(caminho => ({
      caminho, tipo: 'ATUALIZAR', externalCode: null
    } as DiferencaCanalVendaVM));
  }
  const primeiroPreco = diferencas.findIndex(item => CAMPOS_PRECO.includes(item.caminho));
  return diferencas.filter((item, indice) => !CAMPOS_PRECO.includes(item.caminho) || indice === primeiroPreco).map(diferenca => {
    const {caminho} = diferenca;
    if(CAMPOS_PRECO.includes(caminho)) {
      const alterada = criando || previa.diferencas.some(item => CAMPOS_PRECO.includes(item.caminho));
      return {
        campo: 'Preço', acao: criando ? 'Novo' : alterada ? 'Alterado' : 'Sem alteração', alterada,
        antes: formatePrecoRevisao(previa, 'antes'), depois: formatePrecoRevisao(previa, 'depois'), imagem: false,
        secao: 'PRODUTO' as const
      };
    }
    const antes = previa.publicado ? recurso(previa.publicado, caminho) : diferenca.antes;
    const depois = previa.desejado ? recurso(previa.desejado, caminho) : diferenca.depois;
    const origem = previa.diferencas.find(item => item.caminho === caminho ||
      caminho.startsWith(item.caminho + '.') || caminho.startsWith(item.caminho + '['));
    const alterada = criando || !!origem;
    const tipo = origem?.tipo || diferenca.tipo;
    return {
      campo: nomeCampo(previa, caminho), alterada,
      acao: criando || tipo === 'CRIAR' ? 'Novo' : tipo === 'REMOVER' ? 'Removido' :
        tipo === 'DESATIVAR' ? (caminho === 'comercial.vender' ? 'Desabilitar' : 'Pausar') :
          tipo === 'ATIVAR' ? (caminho === 'comercial.vender' ? 'Habilitar' : 'Ativar') : alterada ? 'Alterado' : 'Sem alteração',
      antes: formateValor(caminho, antes, categorias), depois: formateValor(caminho, depois, categorias),
      imagem: caminho === 'conteudo.imagem',
      secao: caminho === 'conteudo.imagem' ? 'IMAGEM' as const :
        caminho.startsWith('estrutura.') ? 'ADICIONAIS' as const : 'PRODUTO' as const
    };
  });
}
